import { createContext, useContext, useEffect, useState } from 'react';
import { readLocal, writeLocal, KEYS } from '../utils/storage.js';

/**
 * AccessibilityContext — per-tech accessibility prefs.
 *
 * Currently exposes a single toggle: `dyslexicFont` (bool). When true, we
 * add the `font-dyslexic` class to <html>; index.css re-routes body/inputs/
 * buttons to OpenDyslexic. The font binaries are only fetched once the
 * class is set (browser-driven lazy load), so techs who never enable it pay
 * zero cost.
 *
 * Persisted to localStorage under "a11y:dyslexicFont" so each tech keeps
 * their preference across sessions on their iPad.
 */

const AccessibilityContext = createContext({
  dyslexicFont: false,
  setDyslexicFont: () => {},
});

export function AccessibilityProvider({ children }) {
  const [dyslexicFont, setDyslexicFontState] = useState(() => {
    if (typeof window === 'undefined') return false;
    return readLocal(KEYS.dyslexicFont) === '1';
  });

  // Sync class on <html> + persist on every change
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (dyslexicFont) {
      root.classList.add('font-dyslexic');
    } else {
      root.classList.remove('font-dyslexic');
    }
    writeLocal(KEYS.dyslexicFont, dyslexicFont ? '1' : '0');
  }, [dyslexicFont]);

  function setDyslexicFont(next) {
    setDyslexicFontState(!!next);
  }

  return (
    <AccessibilityContext.Provider value={{ dyslexicFont, setDyslexicFont }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  return useContext(AccessibilityContext);
}
